import { inject } from 'mobx-react';
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { Container } from 'reactstrap';

import { modelOf } from '../../../prop-types';
import WishlistStore from '../../../store/WishlistStore';
import { storageKeys } from '../../../util/constants';
import { blockTestRender } from '../../../util/process';
import PageTitle from '../../common/PageTitle';

const hasLocalStorage = () => {
  if (typeof window === 'undefined' || window.isSSR) {
    return false;
  }

  try {
    return Boolean(window.localStorage);
  } catch (error) {
    return false;
  }
};

const readWishlistV2ItemCount = () => {
  if (!hasLocalStorage()) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(storageKeys.wishlistV2);
    if (!raw) {
      return null;
    }

    const document = JSON.parse(raw);
    if (!document || !Array.isArray(document.items)) {
      return null;
    }

    return document.items.length;
  } catch (error) {
    return null;
  }
};

const rowsForItemCount = (itemCount) => {
  if (!itemCount) {
    return { count: 1, height: 50 };
  }

  if (itemCount === 1) {
    return { count: 1, height: 150 };
  }

  return { count: Math.min(itemCount, 6), height: 150 };
};

const getSkeletonRows = (wishlistStore) => {
  // Prefer hydrated store content when it already has items.
  const storeCount =
    wishlistStore && Array.isArray(wishlistStore.items)
      ? wishlistStore.items.length
      : null;

  if (storeCount) {
    return rowsForItemCount(storeCount);
  }

  // Guest loadable fallback often mounts before WishlistStore.load(); prefer V2.
  const storageCount = readWishlistV2ItemCount();
  if (storageCount !== null) {
    return rowsForItemCount(storageCount);
  }

  if (storeCount === 0) {
    return rowsForItemCount(0);
  }

  return { count: 1, height: 50 };
};

const WishListPageSkeleton = ({ wishlistStore }) => {
  const skeletonRows = getSkeletonRows(wishlistStore);

  return (
    <Container className="WishListPage__container">
      <div className="WishListPageSkeleton">
        {blockTestRender() && <Skeleton height={20} width={260} />}
        <div className="Skeleton__spacer" />
        <PageTitle>
          {blockTestRender() && <Skeleton height={40} width={260} />}
        </PageTitle>
        <div className="Skeleton__spacer" />
        {blockTestRender() && (
          <Skeleton count={skeletonRows.count} height={skeletonRows.height} />
        )}
      </div>
    </Container>
  );
};

WishListPageSkeleton.propTypes = {
  wishlistStore: modelOf(WishlistStore),
};

export { getSkeletonRows, readWishlistV2ItemCount, rowsForItemCount };

export default inject('wishlistStore')(WishListPageSkeleton);
